import { useEffect, useMemo, useRef, useState } from 'react'
import ChartPanel from '../charts/ChartPanel'
import {
  TABLE_COLUMNS,
  compareValues,
  enrichTopAd,
  formatTableCell,
  sortByMetric,
} from './topPerformingAds/topPerformingAdsData'
import {
  buildPaidCampaignSummaries,
  buildPaidObjectiveSummaries,
  paidCampaignRowsToSummaries,
  paidObjectiveRowsToSummaries,
} from '../../lib/utils/paidPerformanceReport'
import { normalizePlatformKey } from '../../lib/utils/socialNetworkCatalog'
import HoverTooltip from '../common/tooltip/HoverTooltip'
import KpiInfoTooltip from './components/KpiInfoTooltip'
import HorizontalScrollCards from './components/HorizontalScrollCards'
import './TopPerformingAdsWidget.css'

const VIEW_OPTIONS = [
  { id: 'ads', label: 'Ads' },
  { id: 'campaigns', label: 'Campaigns' },
  { id: 'objectives', label: 'Objective' },
  { id: 'performance', label: 'Performance' },
]

const VIEW_PANEL_HEADINGS = {
  objectives: {
    title: 'Top Objectives',
    subtitle: 'Ranked by revenue, ROAS, and spend.',
  },
  campaigns: {
    title: 'Top Campaigns',
    subtitle: 'Campaign-level performance across selected platforms.',
  },
  performance: {
    title: 'Advertising Performance',
    subtitle: 'Detailed campaign performance by objective.',
  },
  ads: {
    title: 'Top Performing Ads',
    subtitle: 'Highest ROAS ads by selected objective and platform.',
  },
}

const ADS_SORT_OPTIONS = ['roas', 'revenue', 'spend']

function buildCampaignSummaries(ads, objectiveFilter = null, campaigns = []) {
  return buildPaidCampaignSummaries(ads, { objectiveFilter, campaigns })
}

function campaignGroupKey(ad) {
  if (!ad?.campaignId) return ''
  return ad.platformKey ? `${ad.campaignId}|${ad.platformKey}` : String(ad.campaignId)
}

function adMatchesCampaignFilter(ad, campaignFilter) {
  if (!campaignFilter) return true
  return campaignGroupKey(ad) === campaignFilter || String(ad.campaignId) === campaignFilter
}

function buildObjectiveSummaries(ads, campaignRows = []) {
  const groups = new Map()

  ads.forEach((ad) => {
    if (!groups.has(ad.objectiveId)) {
      groups.set(ad.objectiveId, {
        id: ad.objectiveId,
        name: ad.objective,
        ads: [],
      })
    }
    groups.get(ad.objectiveId).ads.push(ad)
  })

  return Array.from(groups.values())
    .map((group) => {
      const campaignSummaries = buildCampaignSummaries(group.ads, null, campaignRows)
      const spend = campaignSummaries.reduce((sum, c) => sum + (Number(c.spend) || 0), 0)
      const revenue = campaignSummaries.reduce((sum, c) => sum + (Number(c.revenue) || 0), 0)
      const roas = spend > 0 ? revenue / spend : 0

      return {
        id: group.id,
        name: group.name,
        campaignCount: campaignSummaries.length,
        adCount: group.ads.length,
        topCampaign: campaignSummaries[0],
        spend,
        revenue,
        roas,
      }
    })
    .sort((a, b) => b.roas - a.roas)
}

const TopPerformingAdsWidget = ({
  ads = [],
  campaigns = [],
  performanceCampaigns,
  objectives = [],
  dataLevel = 'ad',
  loading = false,
  loadingMore = false,
  hasMore = false,
  onLoadMore,
  onViewChange,
  onSortChange,
  onExportSortStateChange,
  platforms,
  onPlatformsChange,
  platformOptions = ['instagram', 'facebook', 'linkedin', 'twitter', 'tiktok'],
  platformLabels = {},
  formatCompactNumber,
  footerActions,
  enabledViews,
  defaultView = 'ads',
  forcedView = null,
}) => {
  const availableViewOptions = useMemo(() => {
    if (!enabledViews?.length) return VIEW_OPTIONS
    return VIEW_OPTIONS.filter((option) => enabledViews.includes(option.id))
  }, [enabledViews])

  const [view, setView] = useState(() => {
    if (defaultView && VIEW_OPTIONS.some((o) => o.id === defaultView)) {
      if (!enabledViews?.length || enabledViews.includes(defaultView)) return defaultView
    }
    const first = enabledViews?.length
      ? VIEW_OPTIONS.find((o) => enabledViews.includes(o.id))?.id
      : defaultView
    return first || 'ads'
  })
  const [viewOpen, setViewOpen] = useState(false)
  const [platformFilterOpen, setPlatformFilterOpen] = useState(false)
  const [sortBy, setSortBy] = useState('roas')
  const [sortDir, setSortDir] = useState('desc')
  const [tableSortBy, setTableSortBy] = useState('impressions')
  const [tableSortDir, setTableSortDir] = useState('desc')
  const [objectiveFilter, setObjectiveFilter] = useState(null)
  const [campaignFilter, setCampaignFilter] = useState(null)
  const [visibleCount, setVisibleCount] = useState(9)

  const viewRef = useRef(null)
  const platformFilterRef = useRef(null)

  useEffect(() => {
    if (!availableViewOptions.some((option) => option.id === view)) {
      setView(availableViewOptions[0]?.id || 'ads')
    }
  }, [availableViewOptions, view])

  useEffect(() => {
    if (!forcedView || !VIEW_OPTIONS.some((option) => option.id === forcedView)) return
    if (enabledViews?.length && !enabledViews.includes(forcedView)) return
    setView(forcedView)
  }, [forcedView, enabledViews])

  const enrichedAds = useMemo(
    () => ads.map((ad) => (ad.fromApi ? ad : enrichTopAd(ad))),
    [ads],
  )

  const platformFilteredAds = useMemo(() => {
    const showAll = platforms?.has('all') || !platforms || platforms.size === 0
    if (showAll) return enrichedAds
    return enrichedAds.filter((ad) => platforms.has(ad.platformKey))
  }, [enrichedAds, platforms])

  const platformFilteredCampaigns = useMemo(() => {
    const rows = Array.isArray(campaigns) ? campaigns : []
    const showAll = platforms?.has('all') || !platforms || platforms.size === 0
    if (showAll) return rows
    const campaignIdsOnPlatform = new Set(
      platformFilteredAds.map((ad) => String(ad.campaignId || '')).filter(Boolean),
    )
    return rows.filter((row) => {
      const key = row.platformKey || normalizePlatformKey(row.platform)
      if (key && platforms.has(key)) return true
      const id = String(row.campaign_id ?? row.campaignId ?? '')
      return id && campaignIdsOnPlatform.has(id)
    })
  }, [campaigns, platforms, platformFilteredAds])

  const platformFilteredPerformanceCampaigns = useMemo(() => {
    const rows = Array.isArray(performanceCampaigns) ? performanceCampaigns : campaigns
    const showAll = platforms?.has('all') || !platforms || platforms.size === 0
    if (showAll) return rows
    return rows.filter((row) => {
      const key = row.platformKey || normalizePlatformKey(row.platform)
      return key && platforms.has(key)
    })
  }, [performanceCampaigns, campaigns, platforms])

  const campaignSummaries = useMemo(() => {
    const showAll = platforms?.has('all') || !platforms || platforms.size === 0
    const useCampaignApiRows =
      Array.isArray(campaigns) &&
      campaigns.length > 0 &&
      (view === 'campaigns' || dataLevel === 'campaign')
    if (useCampaignApiRows) {
      const rows = paidCampaignRowsToSummaries(campaigns)
      const filtered = showAll
        ? rows
        : rows.filter((row) => {
            const key = row.platformKey || normalizePlatformKey(row.platform)
            return platforms.has(key) || platforms.has(row.platformKey)
          })
      if (objectiveFilter) {
        return filtered.filter((row) => row.objective === objectiveFilter)
      }
      return filtered
    }
    return buildCampaignSummaries(platformFilteredAds, objectiveFilter, platformFilteredCampaigns)
  }, [
    view,
    dataLevel,
    campaigns,
    platforms,
    objectiveFilter,
    platformFilteredAds,
    platformFilteredCampaigns,
  ])

  const sortedAds = useMemo(() => {
    let list = [...platformFilteredAds]
    if (objectiveFilter) {
      list = list.filter((ad) => ad.objective === objectiveFilter)
    }
    if (campaignFilter) {
      list = list.filter((ad) => adMatchesCampaignFilter(ad, campaignFilter))
    }
    return sortByMetric(list, sortBy, sortDir)
  }, [platformFilteredAds, objectiveFilter, campaignFilter, sortBy, sortDir])

  const useApiPaging = typeof onLoadMore === 'function'
  const adsTotalCount = sortedAds.length
  const adsMaxVisible = Math.min(15, adsTotalCount)
  const visibleAds = useApiPaging ? sortedAds : sortedAds.slice(0, visibleCount)
  const adsShowLoadMore = useApiPaging
    ? hasMore
    : adsTotalCount > 9 && visibleCount < adsMaxVisible

  const handleLoadMoreClick = () => {
    if (useApiPaging) {
      if (!loadingMore) onLoadMore()
      return
    }
    setVisibleCount((c) => Math.min(c + 5, adsMaxVisible))
  }

  useEffect(() => {
    setVisibleCount(9)
  }, [platforms, sortBy, sortDir, objectiveFilter, campaignFilter])

  useEffect(() => {
    onViewChange?.(view)
  }, [view, onViewChange])

  useEffect(() => {
    onSortChange?.(sortBy)
  }, [sortBy, onSortChange])

  useEffect(() => {
    onExportSortStateChange?.({
      view,
      sortBy,
      sortDir,
      tableSortBy,
      tableSortDir,
    })
  }, [view, sortBy, sortDir, tableSortBy, tableSortDir, onExportSortStateChange])

  useEffect(() => {
    if (view === 'performance') {
      setObjectiveFilter(null)
      setCampaignFilter(null)
    }
  }, [view])

  const objectiveSummaries = useMemo(() => {
    if (view === 'objectives') {
      if (dataLevel === 'objective' && objectives.length > 0) {
        const rows = paidObjectiveRowsToSummaries(objectives)
        const showAll = platforms?.has('all') || !platforms || platforms.size === 0
        return showAll ? rows : rows.filter((row) => !row.platformKey || platforms.has(row.platformKey))
      }
      const rows = buildPaidObjectiveSummaries({
        ads: platformFilteredAds,
        campaigns,
        platforms,
      })
      return rows.length ? rows : []
    }
    return buildObjectiveSummaries(platformFilteredAds, platformFilteredCampaigns)
  }, [
    view,
    dataLevel,
    objectives,
    campaigns,
    platforms,
    platformFilteredAds,
    platformFilteredCampaigns,
  ])

  const sortedCampaignSummaries = useMemo(
    () => sortByMetric(campaignSummaries, sortBy, sortDir),
    [campaignSummaries, sortBy, sortDir],
  )

  const sortedObjectiveSummaries = useMemo(
    () => sortByMetric(objectiveSummaries, sortBy, sortDir),
    [objectiveSummaries, sortBy, sortDir],
  )

  const sortedTableRows = useMemo(() => {
    const column = TABLE_COLUMNS.find((c) => c.key === tableSortBy) || TABLE_COLUMNS[0]
    const sortKey = column.key
    const source = view === 'performance' ? platformFilteredPerformanceCampaigns : platformFilteredAds
    return [...source].sort((a, b) => {
      const cmp = compareValues(a[sortKey], b[sortKey], column.sortType)
      return tableSortDir === 'desc' ? -cmp : cmp
    })
  }, [view, platformFilteredPerformanceCampaigns, platformFilteredAds, tableSortBy, tableSortDir])

  useEffect(() => {
    if (!TABLE_COLUMNS.some((c) => c.key === tableSortBy)) {
      setTableSortBy('roas')
      setTableSortDir('desc')
    }
  }, [tableSortBy])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (viewRef.current && !viewRef.current.contains(event.target)) {
        setViewOpen(false)
      }
      if (platformFilterRef.current && !platformFilterRef.current.contains(event.target)) {
        setPlatformFilterOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleAdsSortClick = (column) => {
    if (sortBy === column) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))
    } else {
      setSortBy(column)
      setSortDir('desc')
    }
  }

  const handleTableSortClick = (columnKey) => {
    if (tableSortBy === columnKey) {
      setTableSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))
    } else {
      setTableSortBy(columnKey)
      setTableSortDir('desc')
    }
  }

  const togglePlatform = (platform, closeMenu = false) => {
    onPlatformsChange((prev) => {
      const next = new Set(prev)
      if (platform === 'all') {
        return new Set(['all'])
      }
      next.delete('all')
      if (next.has(platform)) {
        next.delete(platform)
        return next.size > 0 ? next : new Set(['all'])
      }
      next.add(platform)
      return next
    })
    if (closeMenu) setPlatformFilterOpen(false)
  }

  const handleViewCampaignsForObjective = (objectiveName) => {
    setObjectiveFilter(objectiveName)
    setCampaignFilter(null)
    setView('campaigns')
  }

  const handleViewAdsForObjective = (objectiveName) => {
    setObjectiveFilter(objectiveName)
    setCampaignFilter(null)
    setView('ads')
  }

  const handleViewAdsForCampaign = (campaign) => {
    setObjectiveFilter(campaign.objective)
    setCampaignFilter(campaign.id)
    setView('ads')
  }

  const renderFilterChip = (label, value, onClear, clearLabel) => (
    <span className="topPerformingAdsWidget__filterChip">
      {label}: {value}
      <button
        type="button"
        className="topPerformingAdsWidget__filterChipClear"
        aria-label={clearLabel}
        onClick={onClear}
      >
        ×
      </button>
    </span>
  )

  const renderFilterChips = ({ showObjective = true, showCampaign = true } = {}) => {
    const chips = []
    if (showObjective && objectiveFilter) {
      chips.push(
        renderFilterChip(
          'Objective',
          objectiveFilter,
          () => setObjectiveFilter(null),
          'Clear objective filter',
        ),
      )
    }
    if (showCampaign && campaignFilter) {
      const campaignName =
        platformFilteredAds.find((ad) => adMatchesCampaignFilter(ad, campaignFilter))?.campaignName
        || sortedCampaignSummaries.find((campaign) => campaign.id === campaignFilter)?.name
      if (campaignName) {
        chips.push(
          renderFilterChip(
            'Campaign',
            campaignName,
            () => setCampaignFilter(null),
            'Clear campaign filter',
          ),
        )
      }
    }
    if (chips.length === 0) return null

    return (
      <>
        <span className="topAdsSortSeparator" aria-hidden="true" />
        {chips.map((chip, index) => (
          <span key={index} className="topPerformingAdsWidget__filterChipWrap">
            {chip}
          </span>
        ))}
      </>
    )
  }

  const renderAdsCarousel = () => (
    <HorizontalScrollCards
      className="topAdsList topAdsList--scroll"
      showNavArrows
      onEndReached={useApiPaging && hasMore ? handleLoadMoreClick : undefined}
    >
      {visibleAds.map((ad, index) => (
        <div key={ad.id} className={`topAdsCard ${ad.image ? 'topAdsCard--hasImage' : ''}`}>
          <div className={`topAdsThumb ${ad.image ? 'topAdsThumb--hasImage' : ''}`}>
            {ad.image && <img src={ad.image} alt="" loading="lazy" />}
            <div className="topAdsThumbImage" />
            {index < 3 && <span className="topAdsThumbBadge">AI</span>}
          </div>
          <div className="topAdsBody">
            <div className="topAdsHeader">
              <div className="topAdsTitle">{ad.title}</div>
            </div>
            <div className="topAdsMetrics">
              <div className="topAdsMetric topAdsMetric--format">
                <span className="topAdsMetricLabel">FORMAT</span>
                <span className={`topAdsFormatBadge topAdsFormatBadge--${(ad.format || 'Image').toLowerCase().replace(/\s+/g, '-')}`}>
                  {ad.format || 'Image'}
                </span>
              </div>
              <div className="topAdsMetric">
                <span className="topAdsMetricLabel">ROAS</span>
                <span className="topAdsMetricValue topAdsMetricValue--roas">{ad.roas.toFixed(1)}x</span>
              </div>
              <div className="topAdsMetric">
                <span className="topAdsMetricLabel">REVENUE</span>
                <span className="topAdsMetricValue topAdsMetricValue--revenue">
                  ${formatCompactNumber(ad.revenue)}
                </span>
              </div>
              <div className="topAdsMetric">
                <span className="topAdsMetricLabel">SPEND</span>
                <span className="topAdsMetricValue topAdsMetricValue--spend">
                  ${formatCompactNumber(ad.spend)}
                </span>
              </div>
            </div>
          </div>
        </div>
      ))}
      {adsShowLoadMore && (
        <button
          type="button"
          className="topAdsLoadMoreTile"
          disabled={loadingMore}
          onClick={handleLoadMoreClick}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          {loadingMore ? 'Loading…' : 'Load more'}
        </button>
      )}
    </HorizontalScrollCards>
  )

  const renderSortHeader = (count, countLabel) => (
    <div className="topAdsSortHeader">
      <div className="topAdsSortHeader__left">
        <span className="topAdsSortLabel">Sort by</span>
        {ADS_SORT_OPTIONS.map((column) => (
          <button
            key={column}
            type="button"
            className={`topAdsSortCol ${sortBy === column ? 'topAdsSortCol--active' : ''}`}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              handleAdsSortClick(column)
            }}
          >
            {column.toUpperCase()} {sortBy === column && (sortDir === 'desc' ? '↓' : '↑')}
          </button>
        ))}
        {renderFilterChips()}
      </div>
      <span className="topAdsSortCount">
        {count} {countLabel}
      </span>
    </div>
  )

  const renderAdsSortHeader = () => renderSortHeader(
    adsTotalCount,
    adsTotalCount === 1 ? 'ad' : 'ads',
  )

  const currentViewLabel = availableViewOptions.find((v) => v.id === view)?.label || 'Ads'
  const panelHeading = VIEW_PANEL_HEADINGS[view] || VIEW_PANEL_HEADINGS.ads

  const platformFilterControl = (
    <div className="platformFilterWrapper">
      <div className="platformFilterTabs">
        {['all', ...platformOptions].map((platform) => (
          <button
            key={platform}
            type="button"
            className={`platformFilterTab ${platforms?.has(platform) ? 'platformFilterTab--active' : ''}`}
            onClick={() => togglePlatform(platform)}
          >
            {platform === 'all' ? 'All' : (platformLabels[platform] || platform.charAt(0).toUpperCase() + platform.slice(1))}
          </button>
        ))}
      </div>
      <div ref={platformFilterRef} className="dashboardFilterSelect platformFilterDropdown">
        <button
          className="dashboardFilterSelectButton"
          type="button"
          onClick={() => setPlatformFilterOpen((prev) => !prev)}
        >
          <span className="dashboardFilterSelectLabel">Platform</span>
          <span className="dashboardFilterSelectValue">
            {platforms?.has('all') || !platforms || platforms.size === 0
              ? 'All'
              : Array.from(platforms).map((p) => platformLabels[p] || p).join(', ')}
          </span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
        {platformFilterOpen && (
          <div className="dashboardFilterSelectMenu">
            {['all', ...platformOptions].map((platform) => (
              <button
                key={platform}
                type="button"
                className={`dashboardFilterSelectOption ${platforms?.has(platform) ? 'dashboardFilterSelectOption--active' : ''}`}
                onClick={() => togglePlatform(platform, true)}
              >
                {platform === 'all' ? 'All' : (platformLabels[platform] || platform.charAt(0).toUpperCase() + platform.slice(1))}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )

  const renderStatsBox = (revenue, roas, spend, countRows = []) => (
    <div className="topPerformingAdsEntityCard__statsBox">
      <div className="topPerformingAdsEntityCard__metric">
        <span className="topPerformingAdsEntityCard__metricLabel">Revenue</span>
        <span className="topPerformingAdsEntityCard__metricValue topPerformingAdsEntityCard__metricValue--revenue">
          ${formatCompactNumber(revenue)}
        </span>
      </div>
      <div className="topPerformingAdsEntityCard__metric">
        <span className="topPerformingAdsEntityCard__metricLabel">ROAS</span>
        <span className="topPerformingAdsEntityCard__metricValue topPerformingAdsEntityCard__metricValue--roas">
          {roas.toFixed(1)}x
        </span>
      </div>
      <div className="topPerformingAdsEntityCard__metric">
        <span className="topPerformingAdsEntityCard__metricLabel">Spend</span>
        <span className="topPerformingAdsEntityCard__metricValue">${formatCompactNumber(spend)}</span>
      </div>
      {countRows.length > 0 && (
        <>
          <div className="topPerformingAdsEntityCard__statsBoxDivider" aria-hidden="true" />
          {countRows.map((row) => (
            <div key={row.label} className="topPerformingAdsEntityCard__metric">
              <span className="topPerformingAdsEntityCard__metricLabel">{row.label}</span>
              <span className="topPerformingAdsEntityCard__metricValue">{row.value}</span>
            </div>
          ))}
        </>
      )}
    </div>
  )

  const renderTopCampaignPreview = (topCampaign) => {
    if (!topCampaign) return null

    return (
      <div className="topPerformingAdsEntityCard__previewSection topPerformingAdsEntityCard__previewSection--textOnly">
        <span className="topPerformingAdsEntityCard__previewLabel">Top Campaign</span>
        <div className="topPerformingAdsEntityCard__preview topPerformingAdsEntityCard__preview--textOnly">
          <span className="topPerformingAdsEntityCard__previewName">{topCampaign.name}</span>
        </div>
      </div>
    )
  }

  const renderTopAdPreview = (topAd) => {
    if (!topAd) return null

    return (
      <div className="topPerformingAdsEntityCard__previewSection topPerformingAdsEntityCard__previewSection--textOnly topPerformingAdsEntityCard__previewSection--topAd">
        <span className="topPerformingAdsEntityCard__previewLabel">Top Ad</span>
        <div className="topPerformingAdsEntityCard__preview topPerformingAdsEntityCard__preview--textOnly topPerformingAdsEntityCard__preview--topAd">
          <span className="topPerformingAdsEntityCard__previewName">{topAd.title}</span>
        </div>
      </div>
    )
  }

  const renderAdsView = () => {
    if (sortedAds.length === 0) {
      return <div className="topPerformingAdsWidget__empty">No ads found for this platform.</div>
    }

    return (
      <>
        {renderAdsSortHeader()}
        {renderAdsCarousel()}
      </>
    )
  }

  const renderObjectivesView = () => {
    if (sortedObjectiveSummaries.length === 0) {
      return (
        <>
          {renderSortHeader(0, 'objectives')}
          <div className="topPerformingAdsWidget__empty">No objective data available for this platform.</div>
        </>
      )
    }

    return (
      <>
        {renderSortHeader(
          sortedObjectiveSummaries.length,
          sortedObjectiveSummaries.length === 1 ? 'objective' : 'objectives',
        )}
        <HorizontalScrollCards
          className="topPerformingAdsObjectives topPerformingAdsObjectives--scroll"
          showNavArrows
          onEndReached={useApiPaging && hasMore ? handleLoadMoreClick : undefined}
        >
          {sortedObjectiveSummaries.map((summary) => (
            <div key={summary.id} className="topPerformingAdsObjectiveCard">
              <div className="topPerformingAdsEntityCard__content">
                <div className="topPerformingAdsEntityCard__header">
                  <span className="topPerformingAdsEntityCard__title">{summary.name}</span>
                </div>

                <div className="topPerformingAdsEntityCard__metaRow">
                  <button
                    type="button"
                    className="topPerformingAdsEntityCard__metaLink"
                    onClick={() => handleViewCampaignsForObjective(summary.name)}
                  >
                    {summary.campaignCount}
                    {' '}
                    {summary.campaignCount === 1 ? 'Campaign' : 'Campaigns'}
                  </button>
                  <span className="topPerformingAdsEntityCard__metaSep" aria-hidden="true" />
                  <button
                    type="button"
                    className="topPerformingAdsEntityCard__metaLink"
                    onClick={() => handleViewAdsForObjective(summary.name)}
                  >
                    {summary.adCount}
                    {' '}
                    {summary.adCount === 1 ? 'Ad' : 'Ads'}
                  </button>
                </div>

                {renderStatsBox(summary.revenue, summary.roas, summary.spend, [
                  { label: 'Campaigns', value: summary.campaignCount },
                  { label: 'Ads', value: summary.adCount },
                ])}

                {renderTopCampaignPreview(summary.topCampaign)}
              </div>
            </div>
          ))}
          {useApiPaging && hasMore && (
            <button
              type="button"
              className="topAdsLoadMoreTile"
              disabled={loadingMore}
              onClick={handleLoadMoreClick}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          )}
        </HorizontalScrollCards>
      </>
    )
  }

  const renderCampaignsView = () => {
    const campaignCount = sortedCampaignSummaries.length
    const campaignCountLabel = campaignCount === 1 ? 'campaign' : 'campaigns'

    if (campaignCount === 0) {
      return (
        <>
          {renderSortHeader(0, campaignCountLabel)}
          <div className="topPerformingAdsWidget__empty">No campaigns found for this platform.</div>
        </>
      )
    }

    return (
      <>
        {renderSortHeader(campaignCount, campaignCountLabel)}
        <HorizontalScrollCards
          className="topPerformingAdsCampaigns topPerformingAdsCampaigns--scroll"
          showNavArrows
          onEndReached={useApiPaging && hasMore ? handleLoadMoreClick : undefined}
        >
          {sortedCampaignSummaries.map((campaign) => (
            <div key={campaign.id} className="topPerformingAdsCampaignCard">
              <div className="topPerformingAdsEntityCard__content">
                <div className="topPerformingAdsEntityCard__header">
                  <div className="topPerformingAdsEntityCard__titleStack">
                    <span className="topPerformingAdsEntityCard__title topPerformingAdsEntityCard__title--campaign">
                      {campaign.name}
                    </span>
                    {campaign.platformKey ? (
                      <span className="topPerformingAdsEntityCard__platform">
                        {platformLabels[campaign.platformKey] || campaign.platformKey}
                      </span>
                    ) : null}
                  </div>
                </div>

                <div className="topPerformingAdsEntityCard__metaRow">
                  <button
                    type="button"
                    className="topPerformingAdsObjectiveBadge"
                    onClick={() => handleViewCampaignsForObjective(campaign.objective)}
                  >
                    {campaign.objective}
                  </button>
                  <span className="topPerformingAdsEntityCard__metaSep" aria-hidden="true" />
                  <button
                    type="button"
                    className="topPerformingAdsEntityCard__metaLink"
                    onClick={() => handleViewAdsForCampaign(campaign)}
                  >
                    {campaign.adCount}
                    {' '}
                    {campaign.adCount === 1 ? 'Ad' : 'Ads'}
                  </button>
                </div>

                {renderStatsBox(campaign.revenue, campaign.roas, campaign.spend, [
                  { label: 'Ads', value: campaign.adCount },
                ])}

                {renderTopAdPreview(campaign.topAd)}
              </div>
            </div>
          ))}
          {useApiPaging && hasMore && (
            <button
              type="button"
              className="topAdsLoadMoreTile"
              disabled={loadingMore}
              onClick={handleLoadMoreClick}
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          )}
        </HorizontalScrollCards>
      </>
    )
  }

  const renderPerformanceView = () => {
    const totalRecords = sortedTableRows.length

    if (totalRecords === 0) {
      return <div className="topPerformingAdsWidget__empty">No campaign performance data available.</div>
    }

    return (
      <>
        <div className="topPerformingAdsPerformanceHeader">
          <span className="topPerformingAdsPerformanceHeader__label">All campaigns</span>
          <span className="topAdsSortCount">
            {totalRecords} {totalRecords === 1 ? 'campaign' : 'campaigns'}
          </span>
        </div>
        <div className="topPerformingAdsTableWrap">
          <table className="topPerformingAdsTable">
            <thead>
              <tr>
                {TABLE_COLUMNS.map((column) => (
                  <th key={column.key}>
                    <div className="topPerformingAdsTable__header">
                      <button
                        type="button"
                        className={`topPerformingAdsTable__sortBtn ${tableSortBy === column.key ? 'topPerformingAdsTable__sortBtn--active' : ''}`}
                        onClick={() => handleTableSortClick(column.key)}
                      >
                        {column.label}
                        {tableSortBy === column.key && (tableSortDir === 'desc' ? ' ↓' : ' ↑')}
                      </button>
                      {column.headerTooltip ? (
                        <KpiInfoTooltip
                          content={column.headerTooltip}
                          aria-label={column.headerTooltip}
                          className="topPerformingAdsTable__columnInfo"
                          portaled
                          placement="bottom"
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                        />
                      ) : null}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
                {sortedTableRows.map((ad, index) => (
                <tr key={String(ad.id || `${ad.campaignId || "ad"}-${index}`)}>
                  {TABLE_COLUMNS.map((column) => {
                    const cellText = formatTableCell(ad, column, { formatCompactNumber, platformLabels })
                    const isName = column.key === 'campaignName'
                    const showNameTooltip = isName && cellText && cellText !== '—'
                    return (
                    <td
                      key={column.key}
                      className={
                        isName
                          ? 'topPerformingAdsTable__cell--name'
                          : column.key === 'objective'
                            ? 'topPerformingAdsTable__cell--objective'
                            : undefined
                      }
                    >
                      {showNameTooltip ? (
                        <HoverTooltip
                          content={cellText}
                          placement="above"
                          align="start"
                          variant="light"
                          wrapClassName="topPerformingAdsTable__nameTooltipWrap"
                          ariaLabel={cellText}
                          maxWidth={360}
                          gap={6}
                        >
                          <span className="topPerformingAdsTable__nameText">{cellText}</span>
                        </HoverTooltip>
                      ) : (
                        cellText
                      )}
                    </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {useApiPaging && hasMore && (
          <button
            type="button"
            className="topPerformingAdsWidget__loadMore"
            disabled={loadingMore}
            onClick={handleLoadMoreClick}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        )}
      </>
    )
  }

  const viewportClass = view === 'ads'
    ? 'topPerformingAdsWidget__viewport--ads'
    : view === 'performance'
      ? 'topPerformingAdsWidget__viewport--performance'
      : view === 'campaigns'
        ? 'topPerformingAdsWidget__viewport--campaigns'
        : view === 'objectives'
          ? 'topPerformingAdsWidget__viewport--objectives'
          : ''

  return (
    <ChartPanel
      dataChartId="topPerformingAds"
      title={panelHeading.title}
      subtitle={panelHeading.subtitle}
      loading={loading}
      loadingMessage="Loading paid performance…"
      actions={(
        <div className="topPerformingAdsHeaderActions">
          <div className="topPerformingAdsControls">
            <div ref={viewRef} className="dashboardFilterSelect topPerformingAdsViewSelect">
              <button
                className="dashboardFilterSelectButton topPerformingAdsViewButton"
                type="button"
                onClick={() => setViewOpen((prev) => !prev)}
              >
                <span className="dashboardFilterSelectLabel">View by</span>
                <span className="dashboardFilterSelectValue">{currentViewLabel}</span>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>
              {viewOpen && (
                <div className="dashboardFilterSelectMenu topPerformingAdsViewMenu">
                  {availableViewOptions.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      className={`dashboardFilterSelectOption ${view === option.id ? 'dashboardFilterSelectOption--active' : ''}`}
                      onClick={() => {
                        setView(option.id)
                        setViewOpen(false)
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {platformFilterControl}
          </div>
        </div>
      )}
      footerActions={footerActions}
    >
      <div className="topPerformingAdsWidget">
        <div className={`topPerformingAdsWidget__viewport ${viewportClass}`}>
          {view === 'objectives' && renderObjectivesView()}
          {view === 'campaigns' && renderCampaignsView()}
          {view === 'ads' && renderAdsView()}
          {view === 'performance' && renderPerformanceView()}
        </div>
      </div>
    </ChartPanel>
  )
}

export default TopPerformingAdsWidget
